import React, { useEffect } from 'react';
import {
  Modal,
  Button,
  Typography,
  ModalHeader,
  ButtonIcon,
  ModalBody,
  ModalFooter,
} from '@statsbomb/kitbag-components';
import {
  WhatsNewModalWrapper,
  WhatsNewModalBody,
  WhatsNewModalHeader,
} from './WhatsNew.styles';
import NewMetrics from '../../assets/images/whats-new-new-metrics.png';
import { useUserConfigContext } from '../../utils/providers/UserConfigProvider';
import { useStore } from '../../store/useStore';

function WhatsNew() {
  const { account, save } = useUserConfigContext();
  const isOpen = useStore((s) => s.whatsNewModalOpen);
  const setWhatsNewModalOpen = useStore((s) => s.setWhatsNewModalOpen);
  const checkWhatsNewModalOpen = useStore((s) => s.checkWhatsNewModalOpen);

  useEffect(() => {
    checkWhatsNewModalOpen(account);
  }, [account, checkWhatsNewModalOpen]);

  // Blur any focused element when modal opens
  if (isOpen) {
    setTimeout(() => {
      if (document.activeElement) {
        document.activeElement.blur();
      }
    }, 0);
  }

  const handleClose = async () => {
    setWhatsNewModalOpen(false, {
      save,
      account,
    });
  };

  return (
    <WhatsNewModalWrapper>
      <Modal
        id="whats-new-modal"
        testid="whats-new-modal"
        isOpen={isOpen}
        onClose={handleClose}
        onEscape={handleClose}
        onClickOutside={handleClose}
      >
        <ModalHeader>
          <WhatsNewModalHeader>
            <div>
              <div>What&#39;s New?</div>
              <div className="buttons">
                <ButtonIcon
                  onClick={handleClose}
                  variant="ghost"
                  shape="pill"
                  size="small"
                  icon="Close"
                  title="Cancel"
                />
              </div>
            </div>
          </WhatsNewModalHeader>
        </ModalHeader>
        <ModalBody>
          <WhatsNewModalBody>
            <Typography variant="headingRegular">New Metrics in IQ</Typography>

            <img
              src={NewMetrics}
              alt="New metrics in IQ: Pass Rush Win"
              style={{ maxWidth: '100%', height: 'auto' }}
            />

            <Typography variant="bodyRegular">
              We&#39;ve added several new metrics to help you dive deeper into
              player and team performance across pass rush, pass blocking,
              coverage, and game events. Review the detailed definitions below,
              and see where you can view each metric in IQ. You can then start
              leveraging these powerful new insights right now.
            </Typography>

            <ul>
              <li>
                <strong>Pass Rush Won/Lost &amp; Win %:</strong> a pass rush is
                won when the defender beats the initial block in under 2.5
                seconds. Find it in League &gt; Player Stats, Scout and Player
                Overview (Defensive Line &gt; Primary).
              </li>
              <li>
                <strong>Pass Block Won/Lost &amp; Win %:</strong> a pass block
                is won when the offensive lineman is still engaged with the
                defender 2.5 seconds after the snap. Find it in League &gt;
                Player Stats, Scout and Player Overview (Blocking &gt; Primary).
              </li>
              <li>
                <strong>QB Moved Off Spot:</strong> the QB&#39;s throw location
                differs from the drop location, or they exit the pocket. Find it
                in Team Stats &gt; Offense/Defense &gt; Passing.
              </li>
              <li>
                <strong>Coverage Route Won/Lost &amp; Won %:</strong> a route is
                won when the receiver completes a catch on a play classified as
                a success; for defenders it&#39;s the reverse, won when the
                receiver they cover doesn&#39;t. Find it in Player Stats and
                Scout (Receiving and Defensive Backs &gt; Primary).
              </li>
              <li>
                <strong>Chip Blocks:</strong> a TE or RB engages a defender for
                under one second before running a route. Find it in League &gt;
                Player Stats, Scout (Blocking &gt; Primary) and Player Overview
                for TEs and RBs.
              </li>
              <li>
                <strong>MOFO:</strong> the defense showed an open middle of the
                field at the snap. Find it as a column on Game Overview, and as
                MOFO Shown and MOFO Played in Team Stats &gt; Defense &gt;
                Passing.
              </li>
              <li>
                <strong>Penalty Drawn:</strong> shown in Game Overview, Scout
                and the Player Overview stats table.
              </li>
            </ul>
          </WhatsNewModalBody>
        </ModalBody>
        <ModalFooter>
          <Button onClick={handleClose} variant="primary">
            Get Started
          </Button>
        </ModalFooter>
      </Modal>
    </WhatsNewModalWrapper>
  );
}

const MemoizedWhatsNew = React.memo(WhatsNew);
export default MemoizedWhatsNew;
